'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLinePause } from '@/app/components/icons/BaseLinePause';
import { BaseLinePlay } from '@/app/components/icons/BaseLinePlay';
import { SITE } from '@/app/constants';
import { cn } from '@/app/utils';
import { generateThumbnails, playMediaSafely } from '@/app/utils/client';
import { useUnifiedDrag } from '../_hooks/use-unified-drag';
import { getValueFromPosition } from '../_utils';
import { MultipleSlider } from './MultipleSlider';

interface VideoPreviewProps {
  videoSrc: string;
  totalDuration: number;
  rangeValue: [number, number];
  onRangeChange: (value: [number, number]) => void;
  aspectRatio: number;
  minGapToast: string;
  isMobile?: boolean;
  onRemoveMedia?: () => void;
}

const WEBP_THUMBNAIL_QUALITY = 0.8;

const DurationArrowSrc =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/duration_arrow-c5666366.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/duration_arrow-c5666366.webp';

const thumbnailCount = 5;

export const VideoPreview = ({
  videoSrc,
  totalDuration,
  rangeValue,
  onRangeChange,
  aspectRatio,
  minGapToast,
  isMobile = false,
  onRemoveMedia,
}: VideoPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  /** 视频帧缩略图数组 */
  const [videoImages, setVideoImages] = useState<string[]>([]);
  /** 当前播放时间 */
  const [currentTime, setCurrentTime] = useState(0);
  /** duration-arrow 拖拽状态 */
  const [isDraggingArrow, setIsDraggingArrow] = useState(false);
  const { startDrag } = useUnifiedDrag({ passiveTouchMove: false });

  // 更新当前播放时间
  useEffect(() => {
    const video = videoRef.current;
    if (video && videoSrc) {
      setCurrentTime(rangeValue[0]);
      video.currentTime = rangeValue[0];
    }
  }, [videoSrc, rangeValue]);

  useEffect(() => {
    if (!videoSrc) {
      setVideoImages([]);
      return;
    }

    const generate = async () => {
      const existingVideo = videoRef.current;
      if (!existingVideo) return;

      setVideoImages(Array.from({ length: thumbnailCount }, () => ''));

      generateThumbnails({
        videoSrc,
        containerRef: thumbnailContainerRef,
        aspectRatio,
        quantity: WEBP_THUMBNAIL_QUALITY,
        thumbnailCount,
        scaleRatio: 2,
        onItemSuccess: (index, image) => {
          setVideoImages((prev) => {
            const newImages = [...prev];
            newImages[index] = image;
            return newImages;
          });
        },
      });
    };

    const timer = setTimeout(generate, 300);
    return () => clearTimeout(timer);
  }, [videoSrc, aspectRatio]);

  const handleRangeChange = (value: [number, number]) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    // 如果开始时间发生变化，跳转到新的开始位置
    if (rangeValue[0] !== value[0]) {
      video.currentTime = value[0];
      setCurrentTime(value[0]);
    }
    onRangeChange(value);
  };

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const videoCurrentTime = video.currentTime;
    setCurrentTime(Math.min(videoCurrentTime, rangeValue[1]));

    // 如果播放超出选定范围的结束时间，暂停视频
    const [, endTime] = rangeValue;
    if (videoCurrentTime > endTime) {
      video.pause();
    }
  }, [rangeValue]);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      const [startTime, endTime] = rangeValue;
      const videoCurrentTime = video.currentTime;

      // 如果当前时间不在播放范围内，跳转到开始位置
      if (videoCurrentTime < startTime || videoCurrentTime > endTime) {
        video.currentTime = startTime;
        setCurrentTime(startTime);
      }
      playMediaSafely(video);
    } else {
      video.pause();
      setCurrentTime(video.currentTime);
    }
  };

  const handleArrowDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      setIsDraggingArrow(true);
      const video = videoRef.current;
      if (video && !video.paused) video.pause();

      startDrag(
        e,
        (clientX) => {
          if (!thumbnailContainerRef.current) return;
          const newTime = getValueFromPosition(clientX, thumbnailContainerRef.current, 0, totalDuration);
          const [startTime, endTime] = rangeValue;
          const constrainedTime = Math.max(startTime, Math.min(endTime, newTime));
          setCurrentTime(constrainedTime);
          const v = videoRef.current;
          if (v) v.currentTime = constrainedTime;
        },
        () => {
          setIsDraggingArrow(false);
        },
      );
    },
    [rangeValue, totalDuration, startDrag],
  );

  return (
    <div
      className="flex h-[140px] flex-1 flex-shrink-0 flex-col rounded-12 border border-system-white08 bg-system-white04 backdrop-blur-[30px] sm:w-full sm:flex-auto"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-[88px]">
        <div className="h-full overflow-hidden rounded-t-12 bg-system-black64">
          <video
            src={videoSrc}
            ref={videoRef}
            className="h-full w-full object-contain"
            controls={false}
            preload="auto"
            crossOrigin="anonymous"
            muted
            onPlaying={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onLoadedMetadata={() => {
              const video = videoRef.current;
              if (video) setCurrentTime(video.currentTime);
            }}
            onTimeUpdate={handleTimeUpdate}
          />
        </div>
        {onRemoveMedia && (isMobile || isHovered) && (
          <button
            type="button"
            className="-translate-y-1/2 absolute top-0 right-0 z-10 flex size-5 translate-x-1/2 items-center justify-center rounded-full bg-white/40 text-system-white transition-colors hover:bg-white/70"
            onClick={onRemoveMedia}
          >
            <BaseLineClose className="size-3" />
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 items-center px-2 py-1.5" id="extend-video-frame">
        <button
          type="button"
          className="flex h-full w-6 items-center justify-center text-white"
          onClick={handlePlayPause}
        >
          {isPlaying ? <BaseLinePause className="text-xl" /> : <BaseLinePlay className="text-xl" />}
        </button>

        {videoImages.length === 0 ? (
          <BaseLineLoading2 className="flex-1 animate-spin text-base" />
        ) : (
          <div className="relative ml-1 flex h-full flex-1 items-center justify-center">
            <div ref={thumbnailContainerRef} className="absolute inset-0 flex overflow-hidden rounded-6">
              {videoImages.map((imageUrl, index) => (
                <div
                  key={`thumbnail-${index}-${totalDuration}`}
                  className={cn('h-full flex-grow bg-system-text04', !imageUrl && 'animate-pulse')}
                  style={{ width: `${100 / videoImages.length}%` }}
                >
                  {imageUrl && <img src={imageUrl} alt={`缩略图 ${index}`} className="h-full w-full object-cover" />}
                </div>
              ))}

              <div
                className="absolute top-0 bottom-0 z-20 w-[1px] bg-system-white transition-all duration-100 ease-linear"
                style={{ left: `${(currentTime / totalDuration) * 100}%`, transform: 'translateX(-50%)' }}
              />
              <img
                src={DurationArrowSrc}
                alt="duration-arrow"
                className="absolute top-[-6px] z-20 size-2 cursor-pointer touch-none select-none object-cover transition-all duration-100 ease-linear"
                style={{
                  left: `${(currentTime / totalDuration) * 100}%`,
                  transform: `translateX(-50%) rotate(180deg) ${isDraggingArrow ? 'scale(1.25)' : ''}`,
                }}
                onMouseDown={handleArrowDragStart}
                onTouchStart={handleArrowDragStart}
              />
            </div>

            <MultipleSlider
              className="relative z-10 h-full w-full"
              value={rangeValue}
              onValueChange={handleRangeChange}
              defaultValue={rangeValue}
              min={0}
              max={totalDuration}
              minGap={2}
              step={1}
              showTooltip={true}
              tooltipFormatter={(value) => `${value}s`}
              tooltipSide="top"
              minGapToast={minGapToast}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPreview;
